// Dependencies
import { arrayMove } from "@dnd-kit/sortable";
import { nanoid } from "nanoid/non-secure";
import React, { useCallback, useMemo, useRef, useState } from "react";
import Select, { ActionMeta, GroupBase, components } from "react-select";
import { useDebounceValue, useResizeObserver } from "usehooks-ts";
import { z } from "zod";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown, faCircleXmark, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { EmployeeSearchServiceQueryParams } from "@performyard/service-contracts";

// Hooks
import { useEmployeePickerSearch } from "../../api/hooks";

// Utils
import avatarURL from "../../utils/avatarURL";
import { SingleEmployeeSchema } from "./utils";

// Components
import DeletableChip from "../Chip/templates/DeletableChip";
import DragAndDrop from "../DragAndDrop";
import DraggableEmployee from "../DragAndDrop/DraggableEmployee";
import InputWrapper from "../Inputs/InputWrapper";
// Config
import Config from "../Inputs/SelectInput/Config";
import Text, { TextSize } from "../Text";
// Styles
import css from "./EmployeePicker.module.scss";

export interface BaseProps {
	/**
	 * Optional label for the input
	 */
	label?: string;
	/**
	 * The size of the label
	 */
	labelSize?: TextSize;
	/**
	 * The id of the input
	 */
	inputId?: string;
	/**
	 * Whether the input is disabled or not
	 */
	disabled?: boolean;
	/**
	 * Placeholder text for the input, this will override the default placeholder text
	 */
	placeholder?: string;
	/**
	 * The value of the input
	 */
	value: string | EmployeeOption | EmployeeOption[];
	/**
	 * Left icon to display on the input
	 */
	leftIcon?: IconProp;
	/**
	 * Whether the input is clearable or not
	 */
	isClearable?: boolean;
	/**
	 * Whether or not to open menu on focus
	 */
	openMenuOnFocus?: boolean;
	/**
	 * Whether or not to open menu on click
	 */
	openMenuOnClick?: boolean;
	/**
	 * A function that returns a list of options
	 */
	fetchOptions?: (inputValue: string) => Promise<EmployeeOption[]>;
	/**
	 * A function that is called when the value of the input changes
	 */
	onChange: (value: EmployeeOption | EmployeeOption[] | null, actionMeta?: ActionMeta<EmployeeOption>) => void;
	/**
	 * Options inserted at the beginning of the list
	 */
	prependOptions?: EmployeeOption[];
	/**
	 * Whether the input multi-select or not. If draggableOptions is true, this will be automatically set to true
	 */
	isMultiSelect?: boolean;
	/**
	 * Filters to apply to the search query
	 */
	filters?: { [key: string]: string };
	/**
	 * Whether or not to show a warning for the input
	 */
	showWarning?: boolean;
	/**
	 * A function that is called when an option is removed
	 */
	onRemoveOption?: (employeeValue: string) => void;
	/**
	 * The maximum height of the dropdown menu
	 */
	maxHeight?: number;
	/**
	 * The key to use for the query cache
	 */
	queryKey?: string;
	/**
	 * Whether or not to exclude the current user from the list
	 */
	excludeCurrentUser?: boolean;
	/**
	 * A list of employee IDs to exclude from the list
	 */
	exclusionList?: string[];
	/**
	 * Whether or not to limit the list to direct reports
	 */
	limitToDirectReports?: boolean;
	/**
	 * Whether or not to show the dropdown indicator
	 */
	dropdownIndicator?: boolean;
	/**
	 * Whether or not to show draggable options
	 */
	draggableOptions?: boolean;
	/**
	 * The image renderd to the right of the title
	 */
	titleImage?: string;
	/**
	 * href of the title
	 */
	titleHref?: string;

	/**
	 * Error message to show under the input
	 */
	errorMessage?: string;

	/**
	 * title tooltip
	 */
	titleTooltip?: string;
	searchServiceProps?: Partial<EmployeeSearchServiceQueryParams>;
	version?: "searchService" | "legacy";
	/**
	 * Class override for the employee picker
	 */
	className?: string;
}

export type SingleEmployee = z.infer<typeof SingleEmployeeSchema>;

export interface EmployeeGroup {
	label: string;
	options: SingleEmployee[];
}

export type EmployeeOption = SingleEmployee | EmployeeGroup;

/**
 * The employee option component is used to render the employee options in the
 * dropdown menu. It renders the employee's avatar and name.
 */
const EmployeeOption = (employee: SingleEmployee, removeOption: (employeeValue: string) => void) => {
	return (
		<div className={`${css.employeeOption} ${employee.showRemove ? css.employeeRemoveOption : ""}`}>
			<img className={css.avatar} src={avatarURL(employee.value)} alt={`${employee.label} image`} />
			<Text size="paragraph-3" weight="regular" color="neutral-9" className={css.name}>
				{employee.label}
			</Text>
			{employee.showRemove ? (
				<span
					className={css.clearOptionIcon}
					onClick={(e) => {
						e.stopPropagation();
						removeOption(employee.value);
					}}
				>
					<FontAwesomeIcon icon={faTimesCircle} />
				</span>
			) : null}
		</div>
	);
};

/**
 * We use font awesome to render the clear button
 */
const ClearIndicator = (props: any) => {
	return (
		<components.ClearIndicator {...props}>
			<FontAwesomeIcon icon={faCircleXmark} />
		</components.ClearIndicator>
	);
};

const formatGroupLabel = (data: GroupBase<EmployeeOption>) => (
	<div className={css.optionGroup}>
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{data.label}
		</Text>
	</div>
);

const Menu = (props: any) => {
	return <components.Menu {...props}>{props.children}</components.Menu>;
};

/**
 * Overrides the default ValueContainer component to add a clear button and
 * an icon to the left of the input. The default clear function in react-select
 * does not work the way we want it to, so we override it here. In react-select,
 * the clear button only renders if there is a value, we want it to render if
 * there is a value or if there is text in the input.
 */
const createValueContainer = (createProps: Pick<BaseProps, "leftIcon" | "isClearable">) => {
	const ValueContainer = (props: any) => {
		const handleClearValue = () => {
			props.clearValue();
		};

		const isClearable = createProps.isClearable && (props.selectProps.inputValue.length > 0 || props.hasValue);

		return (
			<components.ValueContainer {...props}>
				{createProps.leftIcon ? (
					<div className={css.searchIcon}>
						<FontAwesomeIcon icon={createProps.leftIcon} />
					</div>
				) : null}
				{props.children}
				{isClearable ? (
					<div className={css.clearText} onClick={handleClearValue}>
						<FontAwesomeIcon icon={faTimesCircle} />
					</div>
				) : null}
			</components.ValueContainer>
		);
	};

	return ValueContainer;
};

/**
 * This is an employee picker component that uses the react-select library. It
 * is a wrapper around the react-select library that provides a consistent look
 * and feel for the application. We use the react-select library because it
 * provides a lot of functionality out of the box and is easy to customize.
 * This component extends that flexibility to the implementation level, but
 * only if necessary. All props have a default behavior except for the onChange
 * and value props.
 *
 * Previous employee pickers used the redux store to store the selected
 * employee as well as cache the employee list. This resulted in a bloated
 * redux store that was difficult to maintain.
 * @param props
 * @returns
 */
const EmployeePicker = (props: BaseProps) => {
	const {
		label,
		labelSize = "paragraph-3",
		value,
		onChange,
		isClearable = false,
		disabled = false,
		placeholder = "Make a selection",
		openMenuOnFocus = false,
		openMenuOnClick = true,
		isMultiSelect = false,
		filters,
		showWarning = false,
		onRemoveOption = () => {},
		maxHeight = 256,
		queryKey = "employeePicker",
		prependOptions = [],
		excludeCurrentUser = false,
		exclusionList = [],
		limitToDirectReports = false,
		dropdownIndicator = true,
		draggableOptions = false,
		titleImage,
		titleHref,
		titleTooltip,
		version,
		searchServiceProps,
		errorMessage,
		className,
	} = props;
	const [inputValue, setInputValue] = useState("");
	const [searchTerm] = useDebounceValue<string>(inputValue, 200);
	const pickerRef = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver<HTMLDivElement>({
		ref: pickerRef,
	});
	const overrideFetch = props.fetchOptions ? () => props.fetchOptions?.(searchTerm) : null;
	// The employee picker needs to fetch the employee list from the API. We
	// use the fetchEmployees function to fetch the employee list.
	const { employeeData, isLoading: isQueryLoading } = useEmployeePickerSearch(
		queryKey,
		{ searchTerm, filters: { ...filters }, excludeCurrentUser, exclusionList, limitToDirectReports },
		overrideFetch,
		version,
		searchServiceProps
	);

	const handleInputChange = (inputValue: string) => {
		setInputValue(inputValue);
	};

	// We use the nanoid library to generate a unique id for the input. This is
	// used for the label and the input itself to ensure that they are linked
	// correctly.
	const inputId = useMemo(() => {
		return props.inputId || nanoid();
	}, [props.inputId]);

	// Some props can change the styles of the component. We use the useMemo
	// hook to memoize the styles so that we don't have to recompute them on
	// every render.
	const styles = useMemo(() => {
		return Config.customStyles({
			leftIcon: props.leftIcon,
			showWarning,
			maxHeight,
		});
	}, [props.leftIcon, isClearable, showWarning, maxHeight]);

	// In addition to props changing some styles, they can also change the
	// behavior of the component. We override the default ValueContainer
	// component to add the clear button and the left icon.
	const ValueContainer = useCallback(
		createValueContainer({
			leftIcon: props.leftIcon,
		}),
		[props.leftIcon, isClearable]
	);

	/**
	 * We use font awesome to render the dropdown indicator
	 */
	const DropdownIndicator = (props: any) => {
		return (
			dropdownIndicator && (
				<components.DropdownIndicator {...props}>
					<FontAwesomeIcon icon={faAngleDown} />
				</components.DropdownIndicator>
			)
		);
	};

	const selectComponents = {
		Menu,
		ValueContainer,
		ClearIndicator,
		DropdownIndicator,
	};

	const multiValues = value as SingleEmployee[];

	// delete function for multivalue options
	const handleDelete = (valToDelete: SingleEmployee) => {
		const multiValues = (value as SingleEmployee[]) || [];
		const filteredValues = multiValues.filter((val) => val.value !== valToDelete.value);
		onChange(filteredValues, { action: "remove-value", removedValue: valToDelete });
	};

	const formatDraggableItems = (employee: SingleEmployee) => {
		return (
			<DraggableEmployee
				key={employee.value}
				employeeId={employee.value}
				employeeName={employee.label}
				onRemove={() => handleDelete(employee)}
				version="new"
			/>
		);
	};

	const onDragEnd = (event) => {
		const { active, over } = event;

		if (active.id !== over.id) {
			const activeIndex = multiValues.findIndex((item) => item.value === active.id);
			const overIndex = multiValues.findIndex((item) => item.value === over.id);
			const newEmployees = arrayMove(multiValues, activeIndex, overIndex);
			onChange(newEmployees);
		}
	};

	const isInLoadingState = isQueryLoading || searchTerm !== inputValue;
	const fullOptions = employeeData ? [...prependOptions, ...employeeData] : [];

	return (
		<div data-testid="employee-picker" ref={pickerRef} className={className}>
			<InputWrapper
				labelSize={labelSize}
				label={label}
				disabled={disabled}
				titleHref={titleHref}
				titleImage={titleImage}
				titleTooltip={titleTooltip}
				errorMessage={errorMessage}
				animatedError
			>
				<Select
					inputId={inputId}
					styles={styles}
					isDisabled={disabled}
					placeholder={placeholder}
					options={!isInLoadingState ? fullOptions : []}
					formatOptionLabel={(employee: SingleEmployee) => EmployeeOption(employee, onRemoveOption)}
					isClearable={isClearable}
					components={selectComponents}
					onChange={onChange as any}
					value={value as any}
					inputValue={inputValue}
					openMenuOnFocus={openMenuOnFocus}
					openMenuOnClick={openMenuOnClick}
					formatGroupLabel={formatGroupLabel}
					isMulti={isMultiSelect || draggableOptions}
					controlShouldRenderValue={!isMultiSelect && !draggableOptions}
					noOptionsMessage={() => null}
					isLoading={isInLoadingState}
					onInputChange={handleInputChange}
				/>

				{isMultiSelect && !draggableOptions ? (
					<div
						// needed for template to grab the people ids
						id="people-custom-field-ids"
						className={css.multiSelectOptions}
						people-ids={isMultiSelect ? multiValues.map((val) => val.value) : []} // eslint-disable-line react/no-unknown-property
					>
						{multiValues.map((value) => (
							<DeletableChip
								className={css.multiValueOption}
								key={value.value}
								label={
									<div style={{ maxWidth: width - 48 }} className={`whitespace-normal break-words`}>
										<p className="line-clamp-1 text-caption font-semi-bold">{value.label}</p>
									</div>
								}
								onDelete={() => handleDelete(value)}
								avatarID={value.value}
							/>
						))}
					</div>
				) : null}
				{draggableOptions ? (
					<DragAndDrop
						itemsContainerClassName={css.dragAndDropContainer}
						itemIds={multiValues.map((employee) => employee.value)}
						draggableItems={multiValues}
						handleOnDragEnd={onDragEnd}
						formatDraggableItems={formatDraggableItems}
						keyValue="value"
					/>
				) : null}
			</InputWrapper>
		</div>
	);
};

export default EmployeePicker;
